import AsyncStorage from '@react-native-async-storage/async-storage';
import type { RootStackParamList } from '@/types/navigation';
import { clientLogger } from './datadog';

const PENDING_INVITE_CODE_KEY = 'pendingInviteCode';
const REDIRECT_AFTER_LOGIN_KEY = 'redirectAfterLogin';
const RECENT_LEAGUES_KEY = 'recentLeagueIds';

export type RedirectAfterLogin = {
  routeName: keyof RootStackParamList;
  params?: RootStackParamList[keyof RootStackParamList];
};

export async function setPendingInviteCode(code: string) {
  await AsyncStorage.setItem(PENDING_INVITE_CODE_KEY, code.toUpperCase());
}

export async function getPendingInviteCode(): Promise<string | null> {
  return AsyncStorage.getItem(PENDING_INVITE_CODE_KEY);
}

export async function clearPendingInviteCode() {
  await AsyncStorage.removeItem(PENDING_INVITE_CODE_KEY);
}

export async function setRedirectAfterLogin(redirect: RedirectAfterLogin) {
  await AsyncStorage.setItem(REDIRECT_AFTER_LOGIN_KEY, JSON.stringify(redirect));
}

export async function getRedirectAfterLogin(): Promise<RedirectAfterLogin | null> {
  const storedValue = await AsyncStorage.getItem(REDIRECT_AFTER_LOGIN_KEY);
  if (!storedValue) {
    return null;
  }

  try {
    return JSON.parse(storedValue) as RedirectAfterLogin;
  } catch (error) {
    clientLogger.warn('[storage] Failed to parse redirect payload', {
      error: error instanceof Error ? error.message : String(error),
    });
    return null;
  }
}

export async function clearRedirectAfterLogin() {
  await AsyncStorage.removeItem(REDIRECT_AFTER_LOGIN_KEY);
}

export async function recordRecentLeague(leagueId: string | number, maxEntries = 3) {
  const normalizedId = String(leagueId);
  try {
    const raw = await AsyncStorage.getItem(RECENT_LEAGUES_KEY);
    const parsed: string[] = raw ? JSON.parse(raw) : [];
    const deduped = [normalizedId, ...parsed.filter((existing) => existing !== normalizedId)].slice(
      0,
      maxEntries,
    );
    await AsyncStorage.setItem(RECENT_LEAGUES_KEY, JSON.stringify(deduped));
  } catch (error) {
    clientLogger.warn('[storage] Failed to record recent league id', {
      leagueId: normalizedId,
      error: error instanceof Error ? error.message : String(error),
    });
  }
}

export async function getRecentLeagueIds(): Promise<string[]> {
  try {
    const raw = await AsyncStorage.getItem(RECENT_LEAGUES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed.map((id) => String(id));
    }
    return [];
  } catch (error) {
    clientLogger.warn('[storage] Failed to read recent league ids', {
      error: error instanceof Error ? error.message : String(error),
    });
    return [];
  }
}
